import React, { useState, useEffect } from 'react';
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';
import ClientHeader from '../../Common/Layout/ClientHeader';
import { rexTechTheme } from '../../Common/Theme';
import { 
  FileText,
  Download,
  Search,
  Filter,
  Calendar,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Printer,
  FileSpreadsheet,
  Building
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const ClientReport = () => {
    const [reports, setReports] = useState([]);
    const [filteredReports, setFilteredReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Filter states
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        status: ''
    });
    
    // Status options
    const [statusOptions] = useState([
        'NEW', 'COLLECTOR ASSIGNED', 'COLLECTED', 'DELIVERED', 'RECYCLED'
    ]);

    useEffect(() => {
        fetchData();
    }, []);


const fetchData = async () => {
    try {
        setIsLoading(true);
        
        // Get userId from localStorage
        const userId = localStorage.getItem('userId');
        
        // Build query parameters from filters
        const queryParams = new URLSearchParams();
        
        // Always add userId from localStorage if it exists
        if (userId) {
            queryParams.append('userId', userId);
        }
        
        // Add filters as query parameters only if they have values
        Object.entries(filters).forEach(([key, value]) => {
            if (value) {
                queryParams.append(key, value);
            }
        });
        
        // Make GET request with query parameters
        const url = `${config.APIACTIVEURL}${config.GETREQUESTREPORT}?${queryParams.toString()}`;
        const response = await axios.get(url);
        
        setReports(response.data || []);
        setFilteredReports(response.data || []);
    } catch (error) {
        console.error("Error fetching reports", error);
        showToast('error', 'Failed to load reports');
    } finally {
        setIsLoading(false);
    }
};
    // const fetchData = async () => {
    //     try {
    //         setIsLoading(true);
            
    //         // Build query parameters from filters
    //         const queryParams = new URLSearchParams();
            
    //         // Add filters as query parameters only if they have values
    //         Object.entries(filters).forEach(([key, value]) => {
    //             if (value) {
    //                 queryParams.append(key, value);
    //             }
    //         });
            
    //         // Make GET request with query parameters
    //         const url = `${config.APIACTIVEURL}${config.GETREQUESTREPORT}?${queryParams.toString()}`;
    //         const response = await axios.get(url);
            
    //         setReports(response.data || []);
    //         setFilteredReports(response.data || []);
    //     } catch (error) {
    //         console.error("Error fetching reports", error);
    //         showToast('error', 'Failed to load reports');
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyFilters = () => {
        fetchData();
        setShowFilters(false);
    };

    const resetFilters = () => {
        setFilters({
            startDate: '',
            endDate: '',
            status: ''
        });
        setSearchTerm('');
        fetchData();
    };


    const showToast = (type, message) => {
        // Implement toast notification here
        console.log(`${type}: ${message}`);
    };

    // ================= PDF Export Functions =================

    const exportToPDF = () => {
        try {
            const doc = new jsPDF('protrait');
            const pageWidth = doc.internal.pageSize.width;
            
            // Add title
            doc.setFontSize(20);
            doc.setTextColor(30, 132, 73);
            doc.text('CLIENT REPORTS', pageWidth / 2, 20, { align: 'center' });
            
            // Add subtitle with date range
            doc.setFontSize(11);
            doc.setTextColor(100, 100, 100);
            
            let dateRange = 'All Time Reports';
            if (filters.startDate || filters.endDate) {
                const start = filters.startDate ? new Date(filters.startDate).toLocaleDateString() : 'Start';
                const end = filters.endDate ? new Date(filters.endDate).toLocaleDateString() : 'End';
                dateRange = `Period: ${start} to ${end}`;
            }
            doc.text(dateRange, pageWidth / 2, 28, { align: 'center' });
            
            // Add generation date
            doc.text(`Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, pageWidth / 2, 35, { align: 'center' });
            
            // Prepare table data
            const tableData = filteredReports.map(report => [
                report.pickupDate ? new Date(report.pickupDate).toLocaleDateString() : 'N/A',
                report.lastUpdated ? new Date(report.lastUpdated).toLocaleDateString() : 'N/A',
                report.status || 'N/A',
                report.itemsSent?.toString() || '0',
                report.recycledWeight ? `${report.recycledWeight.toFixed(2)}` : 'N/A',
                report.totalPallets?.toString() || '0'
            ]);
            
            // Calculate column widths based on content
            const columnStyles = {
             
                0: { cellWidth: 25, halign: 'center' },  // Pickup Date
                1: { cellWidth: 25, halign: 'center' },  // Last Updated
                2: { cellWidth: 25, halign: 'center' },  // Status
                3: { cellWidth: 20, halign: 'center' },  // Items Sent
                4: { cellWidth: 25, halign: 'center' },  // Recycled Weight
                5: { cellWidth: 20, halign: 'center' }   // Total Pallets
            };
            
            // Add table with proper styling
            autoTable(doc, {
                head: [[
                  
                    'Pickup Date', 
                    'Last Updated', 
                    'Status', 
                    'Items', 
                    'Recycled (kg)', 
                    'Pallets'
                ]],
                body: tableData,
                startY: 45,
                theme: 'striped',
                headStyles: {
                    fillColor: [30, 132, 73],
                    textColor: 255,
                    fontStyle: 'bold',
                    halign: 'center',
                    fontSize: 10
                },
                bodyStyles: {
                    fontSize: 9,
                    cellPadding: 5,
                    lineColor: [200, 200, 200],
                    lineWidth: 0.1
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                columnStyles: columnStyles,
                margin: { left: 10, right: 10 },
                styles: {
                    overflow: 'linebreak',
                    cellWidth: 'wrap'
                },
                didDrawPage: function (data) {
                    // Add page numbers
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(10);
                    doc.setTextColor(100, 100, 100);
                    doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth - 20, doc.internal.pageSize.height - 10);
                }
            });
            
            // Add summary statistics
            const finalY = doc.lastAutoTable.finalY + 15;
            
            doc.setFontSize(12);
            doc.setTextColor(30, 132, 73);
            doc.text('SUMMARY STATISTICS', 15, finalY);
            
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            
            const summaries = [
                `Total Requests: ${filteredReports.length}`,
                `Total Items Sent: ${filteredReports.reduce((sum, r) => sum + (parseInt(r.itemsSent) || 0), 0)}`,
                `Total Recycled Weight: ${filteredReports.reduce((sum, r) => sum + (parseFloat(r.recycledWeight) || 0), 0).toFixed(2)} kg`,
                `Total Pallets: ${filteredReports.reduce((sum, r) => sum + (parseInt(r.totalPallets) || 0), 0)}`
            ];
            
            summaries.forEach((summary, index) => {
                doc.text(summary, 15, finalY + 10 + (index * 6));
            });
            
            // Add footer
            doc.setFontSize(8);
            doc.setTextColor(150, 150, 150);
            doc.text(`© ${new Date().getFullYear()} RexTech - Generated Report`, pageWidth / 2, doc.internal.pageSize.height - 5, { align: 'center' });
            
            // Save the PDF
            const fileName = `Client-Reports-${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            
            showToast('success', `PDF exported successfully: ${fileName}`);
            
        } catch (error) {
            console.error("Error generating PDF:", error);
            showToast('error', 'Failed to generate PDF');
        }
    };

    const exportToCSV = () => {
        const headers = [
            'Pickup Date', 'Last Updated', 'Status',
            'Items Sent', 'Recycled Weight', 'Total Pallets'
        ];
        
        const csvData = filteredReports.map(report => [
            
            new Date(report.pickupDate).toLocaleDateString(),
            report.lastUpdated ? new Date(report.lastUpdated).toLocaleDateString() : '',
            report.status,
            report.itemsSent,
            report.recycledWeight || '',
            report.totalPallets
        ]);
        
        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Client-Reports-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    // ================= UI Components =================

    const LoadingSpinner = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: rexTechTheme.spacing.xxl,
            color: rexTechTheme.colors.primary.main
        }}>
            <div style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${rexTechTheme.colors.primary.lightest}`,
                borderTop: `3px solid ${rexTechTheme.colors.primary.main}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: rexTechTheme.spacing.md
            }} />
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
            <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>
                Loading client reports...
            </span>
        </div>
    );

    const EmptyState = () => (
        <div style={{
            textAlign: 'center',
            padding: rexTechTheme.spacing.xxl,
            color: rexTechTheme.colors.neutral.darkGray
        }}>
            <FileText size={48} style={{ 
                marginBottom: rexTechTheme.spacing.md,
                color: rexTechTheme.colors.neutral.gray,
                opacity: 0.5
            }} />
            <h3 style={{ 
                color: rexTechTheme.colors.neutral.darkGray,
                marginBottom: rexTechTheme.spacing.sm
            }}>
                No Client Reports Found
            </h3>
            <p style={{ marginBottom: rexTechTheme.spacing.lg }}>
                Try adjusting your filters or check back later
            </p>
            <button
                onClick={resetFilters}
                style={{
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                    backgroundColor: rexTechTheme.colors.primary.lightest,
                    color: rexTechTheme.colors.primary.main,
                    border: `1px solid ${rexTechTheme.colors.primary.light}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    ':hover': {
                        backgroundColor: rexTechTheme.colors.primary.light,
                        color: rexTechTheme.colors.neutral.white
                    }
                }}
            >
                <RefreshCw size={18} style={{ marginRight: rexTechTheme.spacing.sm, verticalAlign: 'middle' }} />
                Reset Filters
            </button>
        </div>
    );

    const StatusBadge = ({ status }) => {
        const statusColors = {
            'NEW': { bg: '#e3f2fd', color: '#1976d2', border: '#90caf9' },
            'COLLECTOR ASSIGNED': { bg: '#fff3e0', color: '#f57c00', border: '#ffb74d' },
            'COLLECTED': { bg: '#e8f5e9', color: '#388e3c', border: '#81c784' },
            'DELIVERED': { bg: '#f3e5f5', color: '#7b1fa2', border: '#ba68c8' },
            'RECYCLED': { bg: '#e0f2f1', color: '#00796b', border: '#4db6ac' }
        };
        
        const style = statusColors[status] || { bg: '#f5f5f5', color: '#757575', border: '#e0e0e0' };
        
        return (
            <span style={{
                padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.md}`,
                backgroundColor: style.bg,
                color: style.color,
                border: `1px solid ${style.border}`,
                borderRadius: rexTechTheme.borderRadius.small,
                fontSize: rexTechTheme.typography.fontSize.small,
                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
            }}>
                {status}
            </span>
        );
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: rexTechTheme.colors.neutral.lightGray,
            fontFamily: rexTechTheme.typography.fontFamily
        }}>
            <ClientHeader />
            
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                padding: rexTechTheme.spacing.xl
            }}>
                {/* Header Section */}
                <div style={{
                    marginBottom: rexTechTheme.spacing.xl
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: rexTechTheme.spacing.lg
                    }}>
                        <div>
                            <h1 style={{
                                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                Client Reports
                            </h1>
                            <p style={{
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                View and export client request data
                            </p>
                        </div>
                        
                        <div style={{ display: 'flex', gap: rexTechTheme.spacing.md }}>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                style={{
                                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                    backgroundColor: rexTechTheme.colors.neutral.white,
                                    color: rexTechTheme.colors.primary.dark,
                                    border: `1px solid ${rexTechTheme.colors.primary.light}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.sm,
                                    ':hover': {
                                        backgroundColor: rexTechTheme.colors.primary.lightest
                                    }
                                }}
                            >
                                <Filter size={18} />
                                {showFilters ? 'Hide Filters' : 'Show Filters'}
                            </button>
                            
                            <button
                                onClick={exportToPDF}
                                style={{
                                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                    color: rexTechTheme.colors.neutral.white,
                                    border: 'none',
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.sm,
                                    boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                    ':hover': {
                                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                    }
                                }}
                            >
                                <Printer size={18} />
                                Export PDF
                            </button>
                            
                            <button
                                onClick={exportToCSV}
                                style={{
                                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                    backgroundColor: rexTechTheme.colors.secondary.main,
                                    color: rexTechTheme.colors.neutral.white,
                                    border: 'none',
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.sm,
                                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                                    ':hover': {
                                        backgroundColor: rexTechTheme.colors.secondary.dark,
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)'
                                    }
                                }}
                            >
                                <FileSpreadsheet size={18} />
                                Export CSV
                            </button>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div style={{
                        backgroundColor: rexTechTheme.colors.neutral.white,
                        borderRadius: rexTechTheme.borderRadius.large,
                        padding: rexTechTheme.spacing.lg,
                        boxShadow: rexTechTheme.shadows.light,
                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                        marginBottom: showFilters ? rexTechTheme.spacing.md : 0
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: rexTechTheme.spacing.md,
                            alignItems: 'center'
                        }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <Search 
                                    size={20} 
                                    style={{
                                        position: 'absolute',
                                        left: rexTechTheme.spacing.md,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: rexTechTheme.colors.neutral.darkGray
                                    }}
                                />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder=" status..."
                                    style={{
                                        width: '100%',
                                        padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                        paddingLeft: `calc(${rexTechTheme.spacing.xl} + 20px)`,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        transition: 'all 0.2s ease',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        ':focus': {
                                            borderColor: rexTechTheme.colors.primary.main,
                                            boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                        }
                                    }}
                                />
                            </div>
                            
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.small
                            }}>
                                <FileText size={16} />
                                <span>{filteredReports.length} records found</span>
                            </div>
                        </div>
                    </div>

                    {/* Filters Panel */}
                    {showFilters && (
                        <div style={{
                            backgroundColor: rexTechTheme.colors.neutral.white,
                            borderRadius: rexTechTheme.borderRadius.large,
                            padding: rexTechTheme.spacing.lg,
                            boxShadow: rexTechTheme.shadows.light,
                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                            marginBottom: rexTechTheme.spacing.lg
                        }}>
                            <h4 style={{
                                color: rexTechTheme.colors.primary.dark,
                                marginBottom: rexTechTheme.spacing.lg,
                                fontSize: rexTechTheme.typography.fontSize.large,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold
                            }}>
                                <Filter size={18} style={{ marginRight: rexTechTheme.spacing.sm, verticalAlign: 'middle' }} />
                                Filter Reports
                            </h4>
                            
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                gap: rexTechTheme.spacing.md,
                                marginBottom: rexTechTheme.spacing.lg
                            }}>
                                {/* Date Range */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        fontSize: rexTechTheme.typography.fontSize.small
                                    }}>
                                        <Calendar size={14} style={{ marginRight: rexTechTheme.spacing.xs, verticalAlign: 'middle' }} />
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={filters.startDate}
                                        onChange={handleFilterChange}
                                        style={{
                                            width: '100%',
                                            padding: rexTechTheme.spacing.md,
                                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            transition: 'all 0.2s ease',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            ':focus': {
                                                borderColor: rexTechTheme.colors.primary.main,
                                                boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                            }
                                        }}
                                    />
                                </div>
                                
                                <div>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        fontSize: rexTechTheme.typography.fontSize.small
                                    }}>
                                        <Calendar size={14} style={{ marginRight: rexTechTheme.spacing.xs, verticalAlign: 'middle' }} />
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={filters.endDate}
                                        onChange={handleFilterChange}
                                        style={{
                                            width: '100%',
                                            padding: rexTechTheme.spacing.md,
                                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            transition: 'all 0.2s ease',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            ':focus': {
                                                borderColor: rexTechTheme.colors.primary.main,
                                                boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                            }
                                        }}
                                    />
                                </div>
                                
                                {/* Status Dropdown */}
                                <div>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        fontSize: rexTechTheme.typography.fontSize.small
                                    }}>
                                        Status
                                    </label>
                                    <select
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                        style={{
                                            width: '100%',
                                            padding: rexTechTheme.spacing.md,
                                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            transition: 'all 0.2s ease',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            backgroundColor: rexTechTheme.colors.neutral.white,
                                            ':focus': {
                                                borderColor: rexTechTheme.colors.primary.main,
                                                boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                            }
                                        }}
                                    >
                                        <option value="">All Statuses</option>
                                        {statusOptions.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            
                            <div style={{
                                display: 'flex',
                                gap: rexTechTheme.spacing.md,
                                justifyContent: 'flex-end'
                            }}>
                                <button
                                    onClick={resetFilters}
                                    style={{
                                        padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                        backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                        color: rexTechTheme.colors.neutral.darkGray,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm,
                                        ':hover': {
                                            backgroundColor: rexTechTheme.colors.neutral.gray
                                        }
                                    }}
                                >
                                    <RefreshCw size={16} />
                                    Reset Filters
                                </button>
                                
                                <button
                                    onClick={applyFilters}
                                    style={{
                                        padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                        color: rexTechTheme.colors.neutral.white,
                                        border: 'none',
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm,
                                        boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                        ':hover': {
                                            background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                        }
                                    }}
                                >
                                    <Filter size={16} />
                                    Apply Filters
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Summary Cards */}
                {!isLoading && filteredReports.length > 0 && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: rexTechTheme.spacing.md,
                        marginBottom: rexTechTheme.spacing.lg
                    }}>
                        <div style={{
                            backgroundColor: rexTechTheme.colors.primary.lightest,
                            borderRadius: rexTechTheme.borderRadius.medium,
                            padding: rexTechTheme.spacing.lg,
                            border: `1px solid ${rexTechTheme.colors.primary.light}`
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.md,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                <User size={20} color={rexTechTheme.colors.primary.main} />
                                <h3 style={{
                                    fontSize: rexTechTheme.typography.fontSize.xlarge,
                                    color: rexTechTheme.colors.primary.dark,
                                    fontWeight: rexTechTheme.typography.fontWeight.bold
                                }}>
                                    {filteredReports.length}
                                </h3>
                            </div>
                            <p style={{
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.small,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold
                            }}>
                                Total Requests
                            </p>
                        </div>
                        
                        <div style={{
                            backgroundColor: rexTechTheme.colors.secondary.lightest,
                            borderRadius: rexTechTheme.borderRadius.medium,
                            padding: rexTechTheme.spacing.lg,
                            border: `1px solid ${rexTechTheme.colors.secondary.light}`
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.md,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                <FileText size={20} color={rexTechTheme.colors.secondary.main} />
                                <h3 style={{
                                    fontSize: rexTechTheme.typography.fontSize.xlarge,
                                    color: rexTechTheme.colors.secondary.dark,
                                    fontWeight: rexTechTheme.typography.fontWeight.bold
                                }}>
                                    {filteredReports.reduce((sum, r) => sum + r.itemsSent, 0)}
                                </h3>
                            </div>
                            <p style={{
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.small,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold
                            }}>
                                Total Items Sent
                            </p>
                        </div>
                        
                        <div style={{
                            backgroundColor: '#e8f5e9',
                            borderRadius: rexTechTheme.borderRadius.medium,
                            padding: rexTechTheme.spacing.lg,
                            border: `1px solid #81c784`
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.md,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                <CheckCircle size={20} color="#388e3c" />
                                <h3 style={{
                                    fontSize: rexTechTheme.typography.fontSize.xlarge,
                                    color: '#2e7d32',
                                    fontWeight: rexTechTheme.typography.fontWeight.bold
                                }}>
                                    {filteredReports.reduce((sum, r) => sum + (r.recycledWeight || 0), 0).toFixed(2)}
                                </h3>
                            </div>
                            <p style={{
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.small,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold
                            }}>
                                Total Recycled (kg)
                            </p>
                        </div>
                        
                        <div style={{
                            backgroundColor: '#fff3e0',
                            borderRadius: rexTechTheme.borderRadius.medium,
                            padding: rexTechTheme.spacing.lg,
                            border: `1px solid #ffb74d`
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.md,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                <Building size={20} color="#f57c00" />
                                <h3 style={{
                                    fontSize: rexTechTheme.typography.fontSize.xlarge,
                                    color: '#e65100',
                                    fontWeight: rexTechTheme.typography.fontWeight.bold
                                }}>
                                    {filteredReports.reduce((sum, r) => sum + (parseInt(r.totalPallets) || 0), 0)}
                                </h3>
                            </div>
                            <p style={{
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.small,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold
                            }}>
                                Total Pallets
                            </p>
                        </div>
                    </div>
                )}

                {/* Main Content - Reports Table */}
                <div style={{
                    backgroundColor: rexTechTheme.colors.neutral.white,
                    borderRadius: rexTechTheme.borderRadius.large,
                    boxShadow: rexTechTheme.shadows.light,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    overflow: 'hidden'
                }}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : filteredReports.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                minWidth: '1200px'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: rexTechTheme.colors.primary.lightest,
                                        borderBottom: `2px solid ${rexTechTheme.colors.primary.main}`
                                    }}>
                                       
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'left',
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Pickup Date
                                        </th>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'left',
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Last Updated
                                        </th>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'left',
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Status
                                        </th>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'center',
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Items Sent
                                        </th>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'center',
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Recycled (kg)
                                        </th>
                                        <th style={{
                                            padding: rexTechTheme.spacing.lg,
                                            textAlign: 'center',
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            color: rexTechTheme.colors.primary.dark,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Pallets
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredReports.map((report, index) => (
                                        <tr 
                                            key={report.requestId}
                                            style={{
                                                backgroundColor: index % 2 === 0 
                                                    ? rexTechTheme.colors.neutral.white 
                                                    : rexTechTheme.colors.neutral.lightGray,
                                                borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                transition: 'all 0.2s ease',
                                                ':hover': {
                                                    backgroundColor: rexTechTheme.colors.primary.lightest
                                                }
                                            }}
                                        >
                                           
                                            
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontWeight: rexTechTheme.typography.fontWeight.medium
                                            }}>
                                                {new Date(report.pickupDate).toLocaleDateString()}
                                            </td>
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg
                                            }}>
                                                {report.lastUpdated ? (
                                                    <>
                                                        <div style={{ color: rexTechTheme.colors.primary.dark }}>
                                                            {new Date(report.lastUpdated).toLocaleDateString()}
                                                        </div>
                                                        <div style={{
                                                            fontSize: rexTechTheme.typography.fontSize.xs,
                                                            color: rexTechTheme.colors.neutral.darkGray,
                                                            marginTop: rexTechTheme.spacing.xs
                                                        }}>
                                                            {new Date(report.lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <span style={{ color: rexTechTheme.colors.neutral.gray }}>N/A</span>
                                                )}
                                            </td>
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg
                                            }}>
                                                <StatusBadge status={report.status} />
                                            </td>
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg,
                                                textAlign: 'center',
                                                fontSize: rexTechTheme.typography.fontSize.large,
                                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                                color: rexTechTheme.colors.primary.dark
                                            }}>
                                                {report.itemsSent}
                                            </td>
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg,
                                                textAlign: 'center',
                                                fontSize: rexTechTheme.typography.fontSize.large,
                                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                                color: '#388e3c'
                                            }}>
                                                {report.recycledWeight ? (
                                                    <div>
                                                        <div>{report.recycledWeight.toFixed(2)}</div>
                                                        <div style={{
                                                            fontSize: rexTechTheme.typography.fontSize.xs,
                                                            color: rexTechTheme.colors.neutral.darkGray,
                                                            marginTop: rexTechTheme.spacing.xs
                                                        }}>
                                                            kg
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span style={{ color: rexTechTheme.colors.neutral.gray }}>N/A</span>
                                                )}
                                            </td>
                                            <td style={{
                                                padding: rexTechTheme.spacing.lg,
                                                textAlign: 'center',
                                                fontSize: rexTechTheme.typography.fontSize.large,
                                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                                color: rexTechTheme.colors.secondary.dark
                                            }}>
                                                {report.totalPallets}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClientReport;